import { env } from "@repo/env";
import {
  type CipherGCMTypes,
  createCipheriv,
  createDecipheriv,
  randomBytes,
} from "crypto";

/**
 * Encryption utility for secure token storage.
 * Uses AES-256-GCM which provides both confidentiality and authenticity.
 *
 * The encrypted format is: iv:authTag:ciphertext (all base64 encoded)
 */

const ALGORITHM: CipherGCMTypes = "aes-256-gcm";
const IV_LENGTH = 16; // 128 bits for GCM

/**
 * Convert Buffer to Uint8Array for crypto compatibility.
 */
function bufferToUint8Array(buffer: Buffer): Uint8Array {
  return new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
}

/**
 * Get the encryption key from environment variables.
 * The key must be a 32-byte (64 character) hex string.
 */
function getEncryptionKey(): Uint8Array {
  const keyHex = env.ENCRYPTION_SECRET;

  if (!keyHex) {
    throw new Error("ENCRYPTION_SECRET environment variable is not set");
  }

  // Validate key length (32 bytes = 64 hex characters)
  if (keyHex.length !== 64) {
    throw new Error(
      "ENCRYPTION_SECRET must be exactly 64 hex characters (32 bytes). " +
        "Generate one with: openssl rand -hex 32",
    );
  }

  return bufferToUint8Array(Buffer.from(keyHex, "hex"));
}

/**
 * Encrypt a plaintext string.
 * Returns a string in format: iv:authTag:ciphertext (base64 encoded)
 */
export function encrypt(plaintext: string): string {
  const key = getEncryptionKey();
  const ivBuffer = randomBytes(IV_LENGTH);
  const iv = bufferToUint8Array(ivBuffer);

  const cipher = createCipheriv(ALGORITHM, key, iv);

  let encrypted = cipher.update(plaintext, "utf8", "base64");
  encrypted += cipher.final("base64");

  const authTag = cipher.getAuthTag();

  // Combine iv, authTag, and ciphertext
  return [
    ivBuffer.toString("base64"),
    authTag.toString("base64"),
    encrypted,
  ].join(":");
}

/**
 * Decrypt an encrypted string.
 * Expects input in format: iv:authTag:ciphertext (base64 encoded)
 */
export function decrypt(encryptedData: string): string {
  const key = getEncryptionKey();

  const parts = encryptedData.split(":");
  if (parts.length !== 3) {
    throw new Error("Invalid encrypted data format");
  }

  const [ivBase64, authTagBase64, ciphertext] = parts;

  if (!ivBase64 || !authTagBase64 || !ciphertext) {
    throw new Error("Invalid encrypted data format - missing parts");
  }

  const ivBuffer = Buffer.from(ivBase64, "base64");
  const iv = bufferToUint8Array(ivBuffer);
  const authTagBuffer = Buffer.from(authTagBase64, "base64");
  const authTag = bufferToUint8Array(authTagBuffer);

  const decipher = createDecipheriv(ALGORITHM, key, iv);
  decipher.setAuthTag(authTag);

  let decrypted = decipher.update(ciphertext, "base64", "utf8");
  decrypted += decipher.final("utf8");

  return decrypted;
}
